/**
 * Vue Router configuration
 * ------------------------
 * Defines the URL → component mapping for the single-page application and a
 * few production-quality behaviors:
 *
 *   - Lazy-loaded route components (code-splitting) so the initial bundle is
 *     small and each download page only loads when visited.
 *   - HTML5 history mode for clean URLs (/zoom instead of /#/zoom). NOTE:
 *     this requires the server to fall back to index.html — see README and
 *     the included deploy configs (netlify.toml / vercel.json / _redirects).
 *   - Scroll behavior that returns to the top on navigation and supports
 *     in-page anchor links (#faq) and the browser back button.
 */
import { createRouter, createWebHistory } from 'vue-router'

const routes = [
  {
    // There is no landing page. Each download page is a standalone route and
    // is only reachable through its own URL. We redirect the bare root so "/"
    // is never a dead end — change the target if you prefer Zoom as default.
    path: '/',
    redirect: '/google-meet',
  },
  {
    path: '/google-meet',
    name: 'google-meet',
    // Lazy load: webpack/Vite creates a separate JS chunk for this route.
    component: () => import('@/views/GoogleMeetView.vue'),
    meta: {
      title: 'Download Google Meet — Video calls and meetings for everyone',
      description:
        'Get Google Meet on desktop and mobile. Secure video meetings for Windows, macOS, Android, and iOS.',
    },
  },
  {
    path: '/zoom',
    name: 'zoom',
    component: () => import('@/views/ZoomView.vue'),
    meta: {
      title: 'Download Zoom — Video Conferencing, Web Conferencing',
      description:
        'Download the Zoom Workplace app for Windows, macOS, Linux, iOS, and Android.',
    },
  },
  {
    // Catch-all 404. The `:pathMatch` syntax is the Vue Router 4 way to match
    // any unmatched path.
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('@/views/NotFoundView.vue'),
    meta: { title: 'Page not found — TeachTech Downloads' },
  },
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
  scrollBehavior(to, from, savedPosition) {
    // 1. Browser back/forward → restore previous scroll position.
    if (savedPosition) return savedPosition
    // 2. Anchor link (#section) → smooth-scroll to that element.
    if (to.hash) {
      return { el: to.hash, behavior: 'smooth', top: 80 }
    }
    // 3. Normal navigation → jump to the top.
    return { top: 0 }
  },
})

export default router
